/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useQuery } from '../../Hooks/API/useQuery';

/**
 * Custom useStudioLocator Hook
 */
export const useStudioLocator = (countryCode, studioId) => {
  const { data, isLoading } = useQuery('studios');
  const [currentSelectedMarker, setCurrentSelectedMarker] = useState(null);
  const [mobileDetailsVisible, setMobileDetailsVisible] = useState(false);
  const [studioSpecificLongitude, setStudioSpecificLongitude] = useState(null);
  const [studioSpecificLatitude, setStudioSpecificLatitude] = useState(null);

  /**
     * Set selected marker to state
     * @param marker
     */
  const setSelectedMarker = (marker) => {
    setCurrentSelectedMarker(marker);
    setMobileDetailsVisible(true);
  };

  useEffect(() => {
    if (studioId && data) {
      const foundStudio = data.find((item) => item.id === parseInt(studioId, 10));
      setStudioSpecificLongitude(foundStudio.address.longitude);
      setStudioSpecificLatitude(foundStudio.address.latitude);
      setSelectedMarker(foundStudio);
    }
  }, [studioId, data]);

  /**
     * Toggle the studio details on mobile
     */
  const toggleMobileStudioDetails = () => {
    setMobileDetailsVisible((value) => !value);
    setCurrentSelectedMarker(null);
  };

  /**
   * Map the studio data for the autocomplete
   *
   * @returns {*}
   */
  const getMappedStudioData = () => data.filter((studio) => {
    // Headquarter studios should not be selectable
    const studioIsHeadquarter = studio.studioTags.some((studioTag) => studioTag.name === 'HQ');
    // The Austria Site should only show Austria Studios
    if (countryCode === 'at') {
      const studioIsAustria = studio.address.countryCode === 'Austria';
      return !studioIsHeadquarter && studioIsAustria;
    }
    return !studioIsHeadquarter;
  });

  return {
    isLoading,
    getMappedStudioData,
    currentSelectedMarker,
    setCurrentSelectedMarker,
    setSelectedMarker,
    toggleMobileStudioDetails,
    mobileDetailsVisible,
    studioSpecificLongitude,
    studioSpecificLatitude,
  };
};
